import { useEffect, useMemo } from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PostAudio } from './PostAudio';
import { DraftVideo } from '../lib/useNewPost';
import { PostMediaItem } from '../model/post.types';

type Props = {
  files: File[];
  videos: DraftVideo[];
  onRemoveFile: (file: File) => void;
  onRemoveVideo: (uuid: string) => void;
  attached?: PostMediaItem[];
  onRemoveAttached?: (item: PostMediaItem) => void;
};

const RemoveButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full bg-white text-xs text-gray-1 shadow-[0px_0px_4px_rgba(0,0,0,0.15)]"
  >
    <FontAwesomeIcon icon={faXmark} />
  </button>
);

export const NewPostMediaPreview = ({
  files,
  videos,
  onRemoveFile,
  onRemoveVideo,
  attached = [],
  onRemoveAttached,
}: Props) => {
  const fileItems = useMemo(
    () =>
      files.map((file) => ({
        file,
        url: URL.createObjectURL(file),
        isAudio: file.type.startsWith('audio'),
      })),
    [files],
  );

  useEffect(
    () => () => fileItems.forEach((item) => URL.revokeObjectURL(item.url)),
    [fileItems],
  );

  if (files.length === 0 && videos.length === 0 && attached.length === 0) {
    return null;
  }

  const audioItems = fileItems.filter((item) => item.isAudio);
  const imageItems = fileItems.filter((item) => !item.isAudio);
  const attachedAudio = attached.filter((item) => item.type === 'audio');
  const attachedVisual = attached.filter((item) => item.type !== 'audio');

  return (
    <div className="flex flex-col gap-3">
      {(imageItems.length > 0 ||
        videos.length > 0 ||
        attachedVisual.length > 0) && (
        <div className="flex flex-row flex-wrap gap-3">
          {attachedVisual.map((item, index) => (
            <div
              key={item.input.uuid ?? item.url ?? index}
              className="relative w-fit"
            >
              {onRemoveAttached && (
                <RemoveButton
                  label="Remove attachment"
                  onClick={() => onRemoveAttached(item)}
                />
              )}
              {item.type === 'video' ? (
                <div className="relative size-[138px] overflow-hidden rounded-[10px] bg-black">
                  {item.thumbnailUrl && (
                    <img
                      src={item.thumbnailUrl}
                      alt=""
                      className="size-full object-cover"
                    />
                  )}
                  <div className="pointer-events-none absolute inset-0 grid place-items-center text-white">
                    <FontAwesomeIcon icon={faPlay} />
                  </div>
                </div>
              ) : (
                <img
                  src={item.url}
                  alt=""
                  className="max-h-[138px] rounded-[10px] object-cover"
                />
              )}
            </div>
          ))}
          {imageItems.map((item) => (
            <div key={item.url} className="relative w-fit">
              <RemoveButton
                label={`Remove ${item.file.name}`}
                onClick={() => onRemoveFile(item.file)}
              />
              <img
                src={item.url}
                alt={item.file.name}
                className="max-h-[138px] rounded-[10px] object-cover"
              />
            </div>
          ))}
          {videos.map((video) => (
            <div key={video.uuid} className="relative w-fit">
              <RemoveButton
                label="Remove video"
                onClick={() => onRemoveVideo(video.uuid)}
              />
              <div className="relative size-[138px] overflow-hidden rounded-[10px] bg-black">
                {video.previewUrl &&
                  (video.previewIsVideo ? (
                    <video
                      src={`${video.previewUrl}#t=${
                        video.previewTimeSec && video.previewTimeSec > 0
                          ? video.previewTimeSec
                          : 0.1
                      }`}
                      preload="metadata"
                      muted
                      playsInline
                      className="size-full object-cover"
                    />
                  ) : (
                    <img
                      src={video.previewUrl}
                      alt=""
                      className="size-full object-cover"
                    />
                  ))}
                <div className="pointer-events-none absolute inset-0 grid place-items-center text-white">
                  <FontAwesomeIcon icon={faPlay} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {attachedAudio.map((item) => (
        <PostAudio
          key={item.url}
          url={item.url}
          onRemove={onRemoveAttached ? () => onRemoveAttached(item) : undefined}
        />
      ))}

      {audioItems.map((item) => (
        <PostAudio
          key={item.url}
          url={item.url}
          onRemove={() => onRemoveFile(item.file)}
        />
      ))}
    </div>
  );
};
